import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, ShieldAlert } from 'lucide-react';
import { useParallax } from '../hooks/use-parallax';
import { useInclinacao } from '../hooks/use-inclinacao';

type Cena = {
  sociedade: string;
  indicadores: readonly { rotulo: string; valor: string }[];
  /** Textos dos achados — sempre um IMPEDITIVO e uma EXIGÊNCIA, nesta
   * ordem, porque a coreografia CSS (cores, ícones, fases) é fixa. */
  impede: string;
  exigencia: string;
};

/**
 * Cada volta da cena audita uma SOCIEDADE diferente — nomes ilustrativos,
 * defeitos REAIS (todos são regras de `packages/validador`; o marquee da
 * tira de prova lista as quarenta). A primeira cena é a do pré-render.
 */
const CENAS: readonly Cena[] = [
  {
    sociedade: 'Norte Contabilidade e Serviços LTDA',
    indicadores: [
      { rotulo: 'Sócios', valor: '2' },
      { rotulo: 'Capital social', valor: 'R$ 120.000,00' },
      { rotulo: 'CNAEs', valor: '3' },
      { rotulo: 'Alterações', valor: '1' },
    ],
    impede: 'sócio menor de idade na constituição.',
    exigencia: 'soma das quotas difere do capital social.',
  },
  {
    sociedade: 'Horizonte Cargas e Logística LTDA',
    indicadores: [
      { rotulo: 'Sócios', valor: '3' },
      { rotulo: 'Capital social', valor: 'R$ 450.000,00' },
      { rotulo: 'CNAEs', valor: '5' },
      { rotulo: 'Alterações', valor: '4' },
    ],
    impede: 'CPF de sócio inválido.',
    exigencia: 'capital a integralizar sem prazo.',
  },
  {
    sociedade: 'Vega Engenharia e Projetos SLU',
    indicadores: [
      { rotulo: 'Sócios', valor: '1' },
      { rotulo: 'Capital social', valor: 'R$ 80.000,00' },
      { rotulo: 'CNAEs', valor: '2' },
      { rotulo: 'Alterações', valor: '0' },
    ],
    impede: 'sócio citado sem cadastro.',
    exigencia: 'nome empresarial sem a designação do tipo.',
  },
] as const;

/**
 * Mock do produto — e a ASSINATURA da página: ele audita em loop, uma
 * sociedade diferente por volta.
 *
 * A coreografia é CSS (`cena-*` em `estilos/motion.css`): um relógio só,
 * altura constante, e o estado estático é o quadro FINAL — reduced-motion e
 * o pré-render mostram o cartão parado no veredito. O React só troca os
 * TEXTOS, e troca no único instante em que ninguém vê: o `animationiteration`
 * do veredito dispara na virada do ciclo, quando todas as camadas estão em
 * opacity 0 (fase de calmaria, 0–8%).
 *
 * Movimento: parallax de mouse no desktop (`useParallax`) e inclinação pelo
 * APARELHO no toque (`useInclinacao` — giroscópio no Android, rolagem no
 * iPhone, que exige prompt para o sensor). Os dois entram no mesmo
 * transform; sem eles, `var(--incl-*, 0deg)` devolve o cartão de sempre.
 */
export function MockProduto(): JSX.Element {
  const { x, y } = useParallax(16);
  const cartaoRef = useRef<HTMLDivElement | null>(null);
  useInclinacao(cartaoRef);

  const [indice, setIndice] = useState(0);
  const cena = CENAS[indice] ?? CENAS[0]!;

  useEffect(() => {
    const cartao = cartaoRef.current;
    if (!cartao) return undefined;
    const aoVirarCiclo = (evento: AnimationEvent): void => {
      if (evento.animationName === 'cena-veredito') {
        setIndice((i) => (i + 1) % CENAS.length);
      }
    };
    cartao.addEventListener('animationiteration', aoVirarCiclo);
    return () => {
      cartao.removeEventListener('animationiteration', aoVirarCiclo);
    };
  }, []);

  return (
    <div className="[perspective:1400px]">
      <div
        ref={cartaoRef}
        className="cartao-vidro shadow-auth-profundidade cena-audita flex flex-col gap-5 p-6 transition-transform duration-150 ease-out will-change-transform"
        style={{
          transform: `rotateX(calc(4deg + var(--incl-x, 0deg))) rotateY(calc(-6deg + var(--incl-y, 0deg))) translate3d(${x}px, ${y}px, 0)`,
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-auth-texto-terciario text-[0.65rem] font-medium tracking-[0.14em] uppercase">
              Sociedade
            </p>
            <p className="text-auth-texto text-lg font-light tracking-tight">
              {cena.sociedade}
            </p>
          </div>
          {/*
            DOIS selos empilhados no mesmo grid: "Conferindo…" vive enquanto
            os achados passam, "Em dia" entra com o veredito. `shrink-0` e
            `whitespace-nowrap` pela mesma razão de sempre: selo de estado em
            duas linhas parece defeito.
          */}
          <span aria-hidden className="grid shrink-0">
            <span className="cena-selo cena-selo--conferindo bg-auth-alerta/15 text-auth-alerta rounded-auth-controle px-2.5 py-1 text-xs font-medium">
              Conferindo…
            </span>
            <span className="cena-selo cena-selo--em-dia bg-auth-ok/15 text-auth-ok rounded-auth-controle px-2.5 py-1 text-xs font-medium">
              Em dia
            </span>
          </span>
        </div>

        <dl className="grid grid-cols-2 gap-3">
          {cena.indicadores.map((indicador) => (
            <div
              key={indicador.rotulo}
              className="border-auth-borda/8 bg-auth-superficie/[0.02] rounded-auth-controle border p-3"
            >
              <dt className="text-auth-texto-terciario text-[0.68rem] font-medium tracking-wide">
                {indicador.rotulo}
              </dt>
              <dd className="text-auth-texto mt-1 text-sm font-semibold tabular-nums">
                {indicador.valor}
              </dd>
            </div>
          ))}
        </dl>

        {/*
          O SLOT da cena: altura fixa, camadas absolutas — nada empurra
          layout em nenhum quadro. `min-h` cobre a camada mais alta (duas
          linhas de achado no celular).
        */}
        <div className="border-auth-borda/8 relative min-h-[3.4rem] border-t pt-4">
          <div aria-hidden className="cena-camada cena-camada--achado-1 top-4">
            <ShieldAlert size={18} className="text-auth-erro shrink-0" />
            <p className="text-auth-texto text-sm leading-snug font-medium">
              <span className="text-auth-erro">Impede</span> — {cena.impede}
            </p>
          </div>
          <div aria-hidden className="cena-camada cena-camada--achado-2 top-4">
            <AlertTriangle size={18} className="text-auth-alerta shrink-0" />
            <p className="text-auth-texto text-sm leading-snug font-medium">
              <span className="text-auth-alerta">Exigência</span> — {cena.exigencia}
            </p>
          </div>
          {/* O quadro final — é o que o pré-render serve e o que fica sem
              animação. Único com texto acessível: leitor de tela ouve a
              chegada, não a jornada. */}
          <div className="cena-camada cena-camada--veredito top-4">
            <CheckCircle2 size={18} className="text-auth-ok shrink-0" aria-hidden />
            <p className="text-auth-texto text-sm leading-snug font-medium">
              Nada a corrigir. O contrato pode ser gerado.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
